import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import OccupancyStatisticsData from '../../../types/occupancyStatistics/OccupancyStatisticsData';
import DataTable from '../../../components/common/DataTable/DataTable';
import TableRow, { TableCellData } from '../../../components/common/TableRow/TableRow';
import { SaveDataState } from '../../../redux/reducers/getSaveData/saveDataReducer';
import { getProfileAttributes, putProfileAttributes, resetGetProfileAttributes } from '../../../redux/actions/profile/profile';
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer';
import { ProfileAttributesState } from '../../../redux/reducers/getProfile/profileAttributes/getAttributesReducer';
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer';
import ErrorMessages from '../../../components/common/ErrorMessages/ErrorMessages';
import { useNavigation } from '../../../utils/context/NavigationContext';
import { FormErrorProvider, useFormErrorContext } from '../../../utils/context/FormErrorContext';
import Asterisk from '../../../components/common/Asterisk/Asterisk';
import { commaSeparate } from '../../../utils/formatter';
import { getCurrentCampaignYear, getLastYearSubmittedCampaign, shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from '../../../utils/previousIncomeAttributes';
import { ReadOnlyProvider } from '../../../utils/ReadOnlyContext';
import { GET_PREVIOUS_PROFILE_ATTRIBUTES } from '../../../redux/actionTypes/actionsTypes';
import { selectPreviousProfileAttributes, selectProfileGetAttributes, selectProfileGetOverview, selectSaveData, selectSaveResult } from '../../../redux/selectors/exportSelectorPaths';
import { ControlledAccordion, useAccordionProvider } from '@szhsin/react-accordion'
import AccordionItem, { DefaultAccordionProviderOptions } from '../../../components/common/AccordionItem/AccordionItem'

export interface OccupancyStatisticsProps {
  readonly?: boolean;
  profileAttributes?: Record<string, number | null>;
}

function OccupancyStatistics(props: OccupancyStatisticsProps) {
  const dispatch = useDispatch();
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
  const profileAttributes: ProfileAttributesState = useSelector(selectProfileGetAttributes)
  const previousProfileAttributes: ProfileAttributesState = useSelector(selectPreviousProfileAttributes)
  const saveData: SaveDataState = useSelector(selectSaveData)
  const saveResultState: SaveResultState = useSelector(selectSaveResult)
  const currentSnapshotId = Number(sessionStorage.getItem("currentSnapshotId") as string)
  const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"
  const initialOccupancyStatisticsData: OccupancyStatisticsData = useMemo(() => ({
    potentialResidentDay: null,
    actualResidentDay: null,
    reportedVacancy: null,
    secondOccupantResidentDay: null,
    respiteDay: null,
  }), [])
  const currentCampaignYear = getCurrentCampaignYear()
  const [previousYearData, setPreviousYearData] = useState<Record<string, unknown>>()
  const [previousTwoYearData, setPreviousTwoYearData] = useState<Record<string, unknown>>()

  const [occupancyData, setOccupancyData] = useState<OccupancyStatisticsData>(initialOccupancyStatisticsData)
  const { t } = useTranslation('occupancy_statistics')
  const { direction } = useNavigation()
  const { errors, clearErrors } = useFormErrorContext()
  const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)

  const occupancyStatisticsHeadingsForSummary = {
    'potentialResidentDay': t("header.potentialResidentDay"),
    'actualResidentDay': t("header.actualResidentDay"),
    'reportedVacancy': t("header.reportedVacancy"),
    'secondOccupantResidentDay': t("header.secondOccupantResidentDay"),
    'respiteDay': t("header.respiteDay")
  }

  const occupancyStatisticsHeadings = [
    <th scope="col" data-tip={t("tooltip.potentialResidentDay")} id='potential-resident-days' className='pier-datatable-clear-th' key="occupancy-column-1-header">{t("header.potentialResidentDay")}<Asterisk leftPadding={true} /></th>,
    <th scope="col" data-tip={t("tooltip.actualResidentDay")} id='actual-resident-days' className='pier-datatable-clear-th' key="occupancy-column-2-header">{t("header.actualResidentDay")}<Asterisk leftPadding={true} /></th>,
    <th scope="col" data-tip={t("tooltip.reportedVacancy")} id='reported-vacancy-percent' className='pier-datatable-clear-th' key="occupancy-column-3-header">{t("header.reportedVacancy")}</th>,
    <th scope="col" data-tip={t("tooltip.secondOccupantResidentDay")} id='second-occupant-resident-days' className='pier-datatable-clear-th' key="occupancy-column-4-header">{t("header.secondOccupantResidentDay")}<Asterisk leftPadding={true} /></th>,
    <th scope="col" data-tip={t("tooltip.respiteDay")} id='respite-days' className='pier-datatable-clear-th' key="occupancy-column-5-header">{t("header.respiteDay")}<Asterisk leftPadding={true} /></th>,
  ];

  useEffect(() => {
    if (!props.profileAttributes) {
      dispatch(resetGetProfileAttributes())
      dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId))

      const campaign = getLastYearSubmittedCampaign(profileOverview.profile)

      if (campaign) {
        dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, campaign.currentSnapshotId, GET_PREVIOUS_PROFILE_ATTRIBUTES))
      }
    } else {
      setOccupancyData({
        potentialResidentDay: props.profileAttributes.potentialResidentDay,
        actualResidentDay: props.profileAttributes.actualResidentDay,
        reportedVacancy: props.profileAttributes.reportedVacancy,
        secondOccupantResidentDay: props.profileAttributes.secondOccupantResidentDay,
        respiteDay: props.profileAttributes.respiteDay
      })
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!props.profileAttributes) {
      if (profileAttributes.success) {
        const newOccupancyData = {
          potentialResidentDay: profileAttributes.data.potentialResidentDay,
          actualResidentDay: profileAttributes.data.actualResidentDay,
          reportedVacancy: profileAttributes.data.reportedVacancy,
          secondOccupantResidentDay: profileAttributes.data.secondOccupantResidentDay,
          respiteDay: profileAttributes.data.respiteDay,
        }
        setOccupancyData(newOccupancyData)
      }
    }
  }, [profileAttributes, initialOccupancyStatisticsData, props.profileAttributes])

  const occupancyStatisticsRow: TableCellData[] = [
    { name: "potentialResidentDay", type: "integer", header: "potential-resident-days", forced: true, positiveOnly: true },
    { name: "actualResidentDay", type: "integer", header: "actual-resident-days", forced: true, positiveOnly: true },
    { name: "reportedVacancy", type: "percent", symbol: '%', header: "reported-vacancy-percent", forced: true, positiveOnly: true },
    { name: "secondOccupantResidentDay", type: "integer", header: "second-occupant-resident-days", forced: true, positiveOnly: true },
    { name: "respiteDay", type: "integer", header: "respite-days", forced: true, positiveOnly: true },
  ];

  const handleOccupancyStatisticsChange = (name: keyof OccupancyStatisticsData, value: string | boolean | number | null) => {
    setOccupancyData(oldOccupancyData => ({
      ...oldOccupancyData,
      [name]: value
    }))
  }

  const saveOccupancyData = useCallback(() => {
    clearErrors()
    const fullOccupancyData = { ...profileAttributes.data, ...occupancyData }

    const triggerSpectral = isInternal || direction !== "backward"


    dispatch(putProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId, {
      "profile_attributes": fullOccupancyData,
      "spectral_section": "occ_stats",
      "trigger_spectral": triggerSpectral,
      "is_internal": isInternal
    }))
  }, [currentSnapshotId, dispatch, occupancyData, profileAttributes.data, profileOverview.profile.profile_type_code, direction, clearErrors, isInternal])

  useEffect(() => {
    if (saveData.saving && !props.readonly) {
      saveOccupancyData()
    }
  }, [saveData.saving, saveOccupancyData, props.readonly])

  useEffect(() => {
    if (saveResultState.status === 200) {
      dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId))
    }
  }, [currentSnapshotId, dispatch, profileOverview.profile.profile_type_code, saveResultState.status])


  useEffect(() => {
    let sessionStoragePreviousSnapshotDataInternal = sessionStorage.getItem("previousSnapshotDataInternal" + String(currentCampaignYear - 1))
    if (sessionStoragePreviousSnapshotDataInternal !== null && sessionStoragePreviousSnapshotDataInternal !== undefined) {
      let previousSnapshotData = { profileAttributes: {} }
      previousSnapshotData = JSON.parse(sessionStoragePreviousSnapshotDataInternal)
      setPreviousYearData(previousSnapshotData?.profileAttributes)
    }
    sessionStoragePreviousSnapshotDataInternal = sessionStorage.getItem("previousSnapshotDataInternal" + String(currentCampaignYear - 2))
    if (sessionStoragePreviousSnapshotDataInternal !== null && sessionStoragePreviousSnapshotDataInternal !== undefined) {
      let previousSnapshotData = { profileAttributes: {} }
      previousSnapshotData = JSON.parse(sessionStoragePreviousSnapshotDataInternal)
      setPreviousTwoYearData(previousSnapshotData?.profileAttributes)
    }
  }, [currentCampaignYear])

  return (
    <>{props.profileAttributes ?
      <table className='summary-table occupancy-statistics-table summary-table-headerless'><thead className='invisible-header'><tr><th></th></tr></thead><tbody>
        {Object.keys(occupancyStatisticsHeadingsForSummary).map((key, index) => (
          <tr key={`occupancy-statistics-row-${index}`}>
            <td>
              {occupancyStatisticsHeadingsForSummary[key as keyof typeof occupancyStatisticsHeadingsForSummary]}
            </td>
            <td >
              {commaSeparate(occupancyData[key as keyof OccupancyStatisticsData])}
            </td>
          </tr>
        ))}
      </tbody></table> :
      <div className='partial-width'>
        <div>
          <p className='strong'>{t("caption")}</p>
          <ErrorMessages errors={errors} />
          {isInternal ? <div className='accordion-root'>
            <div className="accordion-extra-wide">
              <ControlledAccordion providerValue={providerValue}>
                <AccordionItem header={String(currentCampaignYear) + " Campaign"} key={'occupancy-statistics-table'} itemKey={'occupancy-statistics-table'}>
                  <DataTable headings={occupancyStatisticsHeadings} className='occupancy-statistics-table'>
                    <TableRow cellData={occupancyStatisticsRow} dataType={occupancyData} onChange={handleOccupancyStatisticsChange} />
                  </DataTable>
                </AccordionItem>
                {(shouldDisplayPreviousYearAttributes() && previousYearData) ? <AccordionItem header={String(currentCampaignYear - 1) + " Campaign"} key={'occupancy-statistics-table-1'} itemKey={'occupancy-statistics-table-1'}>
                  <ReadOnlyProvider isInitiallyReadOnly={true}>
                    <FormErrorProvider>
                      <DataTable headings={occupancyStatisticsHeadings} className='occupancy-statistics-table'>
                        <TableRow cellData={occupancyStatisticsRow} dataType={previousYearData} onChange={() => { console.log("Operation Unsupported") }} />
                      </DataTable>
                    </FormErrorProvider>
                  </ReadOnlyProvider>
                </AccordionItem> : <></>}
                {(shouldDisplayPreviousTwoYearAttributes() && previousTwoYearData) ? <AccordionItem header={String(currentCampaignYear - 2) + " Campaign"} key={'occupancy-statistics-table-2'} itemKey={'occupancy-statistics-table-2'}>
                  <ReadOnlyProvider isInitiallyReadOnly={true}>
                    <FormErrorProvider>
                      <DataTable headings={occupancyStatisticsHeadings} className='occupancy-statistics-table'>
                        <TableRow cellData={occupancyStatisticsRow} dataType={previousTwoYearData} onChange={() => { console.log("Operation Unsupported") }} />
                      </DataTable>
                    </FormErrorProvider>
                  </ReadOnlyProvider>
                </AccordionItem> : <></>}
              </ControlledAccordion>
            </div>
          </div> : <>
            <DataTable headings={occupancyStatisticsHeadings} className='occupancy-statistics-table'>
              <TableRow cellData={occupancyStatisticsRow} dataType={occupancyData} onChange={handleOccupancyStatisticsChange} />
            </DataTable>
            {shouldDisplayPreviousYearAttributes() &&
              <ReadOnlyProvider isInitiallyReadOnly={true}>
                <FormErrorProvider>
                  <br />
                  <DataTable headings={occupancyStatisticsHeadings} className='occupancy-statistics-table'>
                    <TableRow cellData={occupancyStatisticsRow} dataType={previousProfileAttributes.data ? previousProfileAttributes.data as OccupancyStatisticsData : {}} onChange={() => { console.log("Operation Unsupported") }} />
                  </DataTable>
                </FormErrorProvider>
              </ReadOnlyProvider>
            }
          </>
          }
        </div>
      </div>}
    </>
  );
}

export default OccupancyStatistics
